import { normalizeURLSlug, URL_SLUG_LIMIT, validateURLSlug } from '@gitbook/util-url';

import type { RevisionTag, TagColor } from '../types';

/**
 * Generate a normalized slug from a label.
 */
export function generateTagSlug(label: string): string {
    return normalizeURLSlug(label);
}

/**
 * Whether a slug can be stored as-is as a tag key: a URL slug (what
 * `generateTagSlug` produces), and not `__proto__`, which as a plain-object key replaces the
 * object's prototype instead of adding an entry.
 */
export function isValidTagSlug(slug: string): boolean {
    return slug !== '__proto__' && validateURLSlug(slug) && slug.length <= URL_SLUG_LIMIT;
}

export const TAG_LABEL_MAX_LENGTH = 50;

/** Valid tag color values, matching the DocumentTextColor API enum. */
export const VALID_TAG_COLORS = new Set<TagColor>([
    'default',
    'green',
    'blue',
    'red',
    'orange',
    'yellow',
    'purple',
    'pink',
    'violet',
    'cyan',
    '$primary',
    '$secondary',
    '$info',
    '$success',
    '$warning',
    '$danger',
    '$tint',
]);

/**
 * Normalize a tag label by trimming whitespace and max length.
 */
export function normalizeTagLabel(label: string): string {
    return label.trim().slice(0, TAG_LABEL_MAX_LENGTH);
}

/**
 * Compare if two tags are equal based on their properties.
 */
export function compareTags(tag1: RevisionTag, tag2: RevisionTag): boolean {
    return (
        tag1.slug === tag2.slug &&
        tag1.label === tag2.label &&
        tag1.icon === tag2.icon &&
        tag1.emoji === tag2.emoji &&
        tag1.color === tag2.color
    );
}
